import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HistoryItem, saveToHistory } from './ImageHistory';
import { generateImageWithGemini } from '../../../utils/gemini';

interface BatchGenerationProps {
  prompt: string;
  style: string;
  aspectRatio: string;
  onComplete: () => void;
}

export default function BatchGeneration({
  prompt,
  style,
  aspectRatio,
  onComplete,
}: BatchGenerationProps) {
  const { t } = useTranslation();
  const [count, setCount] = useState(4);
  const [isGenerating, setIsGenerating] = useState(false);
  const [results, setResults] = useState<HistoryItem[]>([]);
  const [progress, setProgress] = useState(0);
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null);
  const [generateError, setGenerateError] = useState<string | null>(null);

  const handleBatchGenerate = async () => {
    if (!prompt.trim()) return;
    setGenerateError(null);

    setIsGenerating(true);
    setResults([]);
    setProgress(0);
    setSelectedIdx(null);

    const generated: HistoryItem[] = [];

    try {
      for (let i = 0; i < count; i++) {
        // 使用 Gemini API 生成图片
        const imageUrl = await generateImageWithGemini(prompt, style, aspectRatio);

        const ts = Date.now();
        const item: HistoryItem = {
          id: `batch_${ts}_${i}`,
          imageUrl,
          prompt,
          style,
          aspectRatio,
          createdAt: ts,
        };

        generated.push(item);
        saveToHistory(item);
        setResults([...generated]);
        setProgress(Math.round(((i + 1) / count) * 100));
      }

      onComplete();
    } catch (error: any) {
      console.error('Batch generation failed:', error);
      setGenerateError(error?.message || '批量生成失败，请重试');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownload = async (item: HistoryItem) => {
    try {
      const response = await fetch(item.imageUrl);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ai-batch-${item.id}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch {
      const link = document.createElement('a');
      link.href = item.imageUrl;
      link.download = `ai-batch-${item.id}.jpg`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleDownloadAll = () => {
    results.forEach((item, idx) => {
      setTimeout(() => handleDownload(item), idx * 500);
    });
  };

  return (
    <div className="bg-[#252830] rounded-2xl p-8 shadow-lg border border-gray-800">
      {/* 标题 */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent/20">
          <i className="ri-stack-line text-accent text-lg"></i>
        </div>
        <h3 className="font-black text-white text-base">{t('batch.title')}</h3>
        <span className="px-2.5 py-1 bg-accent/20 rounded-full text-xs font-bold text-accent">{t('batch.badge')}</span>
      </div>

      {generateError && (
        <div className="mb-5 flex items-center gap-3 px-4 py-3 bg-red-500/10 border border-red-500/30 rounded-xl">
          <i className="ri-error-warning-line text-red-400 text-xl"></i>
          <p className="text-red-300 text-sm font-bold">{generateError}</p>
        </div>
      )}

      {/* 数量选择 */}
      <div className="mb-6">
        <label className="block text-sm font-bold text-gray-300 mb-3">{t('batch.countLabel')}</label>
        <div className="flex gap-2">
          {[2, 4, 6, 8].map((n) => (
            <button
              key={n}
              onClick={() => setCount(n)}
              disabled={isGenerating}
              className={`flex-1 px-4 py-3 rounded-xl text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
                count === n ? 'bg-accent text-white shadow-lg' : 'bg-[#1e2025] text-gray-400 hover:bg-[#2d3139] border border-gray-700'
              } ${isGenerating ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {n} {t('batch.images')}
            </button>
          ))}
        </div>
      </div>

      {/* 生成按钮 */}
      <button
        onClick={handleBatchGenerate}
        disabled={!prompt.trim() || isGenerating}
        className={`w-full px-6 py-4 rounded-xl font-black text-base transition-all flex items-center justify-center gap-3 whitespace-nowrap mb-6 ${
          prompt.trim() && !isGenerating
            ? 'bg-accent text-white hover:bg-accent/90 hover:shadow-xl hover:shadow-accent/30 cursor-pointer'
            : 'bg-gray-700 text-gray-500 cursor-not-allowed'
        }`}
      >
        {isGenerating ? (
          <>
            <i className="ri-loader-4-line text-xl animate-spin"></i>
            {t('batch.generating')} ({progress}%)
          </>
        ) : (
          <>
            <i className="ri-stack-line text-xl"></i>
            {t('batch.generateBtn', { count })}
          </>
        )}
      </button>

      {/* 进度条 */}
      {isGenerating && (
        <div className="mb-6">
          <div className="w-full h-2 bg-[#1e2025] rounded-full overflow-hidden">
            <div
              className="h-full bg-accent rounded-full transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="text-xs text-gray-500 font-medium mt-2 text-center">
            {t('batch.progressText', { done: results.length, total: count })}
            <span className="ml-2 text-gray-600">（AI 生成中...）</span>
          </p>
        </div>
      )}

      {/* 结果网格 */}
      {results.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm font-bold text-gray-300">{t('batch.results')} ({results.length})</p>
            {!isGenerating && results.length > 1 && (
              <button
                onClick={handleDownloadAll}
                className="text-xs text-primary hover:text-primary/80 font-bold flex items-center gap-1 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-download-line"></i>
                {t('batch.downloadAll')}
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            {results.map((item, idx) => (
              <div
                key={item.id}
                className={`relative rounded-xl overflow-hidden border-2 transition-all cursor-pointer ${
                  selectedIdx === idx ? 'border-accent shadow-lg' : 'border-gray-700 hover:border-accent/40'
                }`}
                onClick={() => setSelectedIdx(selectedIdx === idx ? null : idx)}
              >
                <div className="aspect-square w-full bg-[#1e2025]">
                  <img
                    src={item.imageUrl}
                    alt={`Variation ${idx + 1}`}
                    className="w-full h-full object-cover object-top"
                    loading="lazy"
                  />
                </div>
                <div className="absolute top-2 left-2 px-2 py-0.5 bg-black/50 backdrop-blur-sm rounded text-[10px] text-white font-bold whitespace-nowrap">
                  #{idx + 1}
                </div>
                {selectedIdx === idx && (
                  <div className="absolute inset-0 bg-accent/10 flex items-end justify-center p-3">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDownload(item); }}
                      className="px-3 py-1.5 bg-white rounded-lg text-xs font-bold text-gray-700 hover:bg-gray-50 transition-all shadow-md cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-download-line mr-1"></i>
                      {t('history.download')}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {results.length === 0 && !isGenerating && (
        <div className="text-center py-6">
          <p className="text-xs text-gray-500 font-medium">{t('batch.hint')}</p>
          <p className="text-xs text-gray-600 mt-1">由 Google Gemini AI 提供支持</p>
        </div>
      )}
    </div>
  );
}